import { Button, Typography } from "@suraasa/placebo-ui"
import { ToolType } from "api/resources/aiTools/types"
import { AISparkleFilled } from "assets/icons/system/AISparkle"
import { allTools, getLastUsedTool, getToolName } from "features/AItools/helper"
import { useFeatureToggle } from "global/FeatureToggleProvider"
import { ArrowRight } from "iconoir-react"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import routes from "routes"
import { BROWSER_STORAGE_KEYS } from "utils/constants"
import { UserPreferenceManager } from "utils/userPreferenceManager"

const homePageAIToolsCards = [
  {
    toolType: ToolType.lessonPlan,
    title: `${getToolName(ToolType.lessonPlan)} Generator`,
    description: "Generate engaging lesson plans in seconds",
    to: routes.aiTools.lessonPlan,
    gradient: "#E5A256",
  },
  {
    toolType: ToolType.narration,
    title: `${getToolName(ToolType.narration)} Generator`,
    description: "Transform your lesson plans into engaging narratives",
    to: routes.aiTools.narration,

    gradient: "#42A491",
  },
  {
    toolType: ToolType.handout,
    title: `${getToolName(ToolType.handout)} Generator`,
    description: "Generate informative lesson handouts effortlessly",
    to: routes.aiTools.handout,
    gradient: "#60A5FA",
  },
  {
    toolType: ToolType.quiz,
    title: `${getToolName(ToolType.quiz)} Generator`,
    description: "Generate accurate quizzes that enhance student learning",
    to: routes.aiTools.quiz,
    gradient: "#97BC62",
  },
]

const Card = ({
  gradient,
  name,
  link,
  isPopular,
}: {
  gradient: string
  name: string
  link: string
  isPopular: boolean
}) => {
  return (
    <Link to={link} className="w-full min-w-[148px]">
      <div className="flex h-full flex-col justify-between rounded-[12px] border border-onSurface-200 bg-onSurface-50 p-2 hover:border-onSurface-300">
        <div className="flex flex-row justify-between">
          <div
            className="mb-1 flex size-4 items-center justify-center rounded-[10px] shadow-[1px_1px_5px_0px_rgba(0,0,0,0.1)]"
            style={{ background: gradient }}
          >
            <AISparkleFilled fill="#ffffff" widths={"18px"} height={"16px"} />
          </div>
          {isPopular && (
            <div className="h-min rounded-lg bg-warning-100 px-0.75 py-0.25">
              <Typography
                variant={"smallBody"}
                className="text-[12px] font-semibold text-warning-700"
              >
                Popular
              </Typography>
            </div>
          )}
        </div>
        <div className="flex flex-col items-start">
          <Typography variant={"strong"} className="mt-3.5 text-onSurface-500">
            Generate
          </Typography>
          <Typography variant={"strong"}>
            {name.replace("Generator", "")}
          </Typography>
        </div>
      </div>
    </Link>
  )
}

const AIToolsSection = () => {
  const featureToggle = useFeatureToggle()

  const isAIToolsEnabled = featureToggle.aiTools.isEnabled

  const lastUsedAITool = UserPreferenceManager.get(
    BROWSER_STORAGE_KEYS.lastUsedAiTool
  )

  const [homePageTools, setHomePageTools] = useState(homePageAIToolsCards)

  useEffect(() => {
    if (lastUsedAITool) {
      const tool = getLastUsedTool({ toolValue: lastUsedAITool })

      if (tool) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setHomePageTools(prevTools => {
          const alreadyExists = prevTools.some(
            t => t.toolType === tool.toolType
          )

          if (!alreadyExists) {
            return [prevTools[0], tool, ...prevTools.slice(1)].slice(0, -1)
          }
          return prevTools
        })
      }
    }
  }, [lastUsedAITool])

  if (!isAIToolsEnabled) return null

  return (
    <div className="mb-3.5">
      <div className="mb-2 flex flex-row justify-between">
        <Typography variant="title3">AI Tools</Typography>
        <div className="flex flex-row gap-0.5">
          <Typography
            variant="smallBody"
            className="!text-[14px] text-onSurface-500"
          >
            Over
          </Typography>
          <Typography
            variant="smallBody"
            className="!text-[14px] font-semibold text-onSurface-500"
          >
            5.5K
          </Typography>
          <Typography
            variant="smallBody"
            className="!text-[14px] text-onSurface-500"
          >
            AI powered creations
          </Typography>
        </div>
      </div>
      <div className="scrollbar-hide flex flex-row gap-1.5 overflow-x-auto">
        {homePageTools.map(el => {
          return (
            <Card
              key={el.title}
              gradient={el.gradient}
              name={el.title}
              link={el.to}
              isPopular={el.title === "Lesson Plan Generator"}
            />
          )
        })}
      </div>
      <div className="mt-2 flex flex-row justify-between">
        <Typography
          variant="smallBody"
          className="!text-[14px] text-onSurface-500"
        >
          +{allTools.length - homePageTools.length} More
        </Typography>
        <Link to={routes.aiTools.home}>
          <Button variant={"text"} endAdornment={<ArrowRight />}>
            Explore More
          </Button>
        </Link>
      </div>
    </div>
  )
}

export default AIToolsSection
